import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { AssistantApi, ChatCompany, ChatMessage, ChatStatus } from '../api/AssistantApi';
import Logo from './Logo';
import AssistantSources from './AssistantSources';
import AssistantImport from './AssistantImport';
import DailySalesPanel from './DailySalesPanel';
import './AssistantChat.css';

export default function AssistantChat() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [dailyOpen, setDailyOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [status, setStatus] = useState<ChatStatus | null>(null);
  const [companies, setCompanies] = useState<ChatCompany[]>([]);
  const [companyId, setCompanyId] = useState('');
  const [deposits, setDeposits] = useState<{ id: number; name: string; companyName: string }[]>([]);
  const [depositId, setDepositId] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companiesLoaded, setCompaniesLoaded] = useState(false);
  const [workspaceError, setWorkspaceError] = useState('');
  const [depositError, setDepositError] = useState('');
  const [depositsLoading, setDepositsLoading] = useState(false);
  const [depositsLimited, setDepositsLimited] = useState(false);
  const [statusError, setStatusError] = useState(false);
  const [reload, setReload] = useState(0);
  const [error, setError] = useState('');
  const request = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const launcher = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const end = useRef<HTMLDivElement>(null);
  const permitted = localStorage.getItem('role') !== 'ROLE_TRANSPORT';
  const scopeReady = permitted && companiesLoaded && companies.length > 0 && !loading;
  const loadError = (failure: unknown, fallback: string) => {
    const code = axios.isAxiosError(failure) ? failure.response?.status : undefined;
    return code === 403 ? 'accessDenied' : code === 404 ? 'backendOutdated' : fallback;
  };

  useEffect(() => () => { generation.current++; request.current?.abort(); }, []);
  useEffect(() => {
    if (!open || !permitted) return;
    const controller = new AbortController();
    setLoading(true);
    setWorkspaceError('');
    setStatus(null);
    setStatusError(false);
    // Model availability must never hide successfully loaded company options.
    AssistantApi.status(controller.signal).then(availability => {
      if (controller.signal.aborted) return;
      if (typeof availability.data.canQuery !== 'boolean') throw new Error('Invalid chat status');
      setStatus(availability.data);
    }).catch(() => { if (!controller.signal.aborted) setStatusError(true); });
    AssistantApi.companies(controller.signal)
      .then(options => {
        if (controller.signal.aborted) return;
        if (!Array.isArray(options.data)) throw new Error('Invalid company list');
        setCompanies(options.data);
        setCompanyId(previous => options.data.some(company => String(company.id) === previous) ? previous : '');
        setCompaniesLoaded(true);
      })
      .catch(failure => { if (!controller.signal.aborted) { setCompanies([]); setCompaniesLoaded(false); setWorkspaceError(loadError(failure, 'companiesUnavailable')); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [open, permitted, reload]);

  useEffect(() => {
    generation.current++;
    request.current?.abort();
    setBusy(false);
    setMessages([]);
    setDraft('');
    setDepositId('');
    setDeposits([]);
    setDepositError('');
    setDepositsLimited(false);
    setDepositsLoading(false);
    if (!companiesLoaded || companies.length === 0) return;
    const controller = new AbortController();
    setDepositsLoading(true);
    AssistantApi.deposits(companyId ? Number(companyId) : undefined, controller.signal).then(response => {
      if (controller.signal.aborted) return;
      const choices = response.data.items.map(item => ({ id: Number(item.depositId), name: String(item.name || ''), companyName: String(item.companyName || '') }));
      setDeposits(choices);
      setDepositsLimited(response.data.hasMore);
    }).catch(failure => { if (!controller.signal.aborted) setDepositError(loadError(failure, 'depositsUnavailable')); })
      .finally(() => { if (!controller.signal.aborted) setDepositsLoading(false); });
    return () => controller.abort();
  }, [companyId, companiesLoaded, companies.length, reload]);

  useEffect(() => { if (open) closeButton.current?.focus(); }, [open]);
  useEffect(() => { if (open) end.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }); }, [messages, busy, open]);

  const close = () => { if (importBusy) return; setOpen(false); setImportOpen(false); setDailyOpen(false); launcher.current?.focus(); };
  const reset = () => {
    if (importBusy) return;
    generation.current++;
    request.current?.abort();
    setBusy(false); setMessages([]); setDraft(''); setError('');
  };
  const send = async (action?: string, question?: string) => {
    const message = action ? t(`chat.prompts.${action}`) : question || draft.trim();
    if (!scopeReady || busy || !message || (!action && !status?.configured) || (request.current && !request.current.signal.aborted)) return;
    const controller = new AbortController();
    request.current = controller;
    const current = ++generation.current;
    const history = messages.slice(-10).map(({ role, content }) => ({ role, content: content.slice(0, 4000) }));
    setMessages(previous => [...previous.slice(-39), { role: 'user', content: message }]);
    setDraft(''); setBusy(true); setError('');
    try {
      const response = await AssistantApi.send({ companyId: companyId ? Number(companyId) : undefined, depositId: depositId ? Number(depositId) : undefined,
        message, action, language: i18n.resolvedLanguage || 'en', history }, controller.signal);
      if (controller.signal.aborted || generation.current !== current) return;
      setMessages(previous => [...previous, { role: 'assistant', content: response.data.answer || t(`chat.actions.${response.data.action}`), reply: response.data }]);
    } catch (failure) {
      if (controller.signal.aborted || generation.current !== current) return;
      const code = axios.isAxiosError(failure) ? failure.response?.status : undefined;
      setError(code === 400 ? 'selectDeposit' : code === 403 || code === 404 ? 'accessDenied' : code === 429 ? 'busyError' : 'unavailable');
      setDraft(message);
    } finally { if (generation.current === current) { setBusy(false); request.current = null; } }
  };

  return (
    <div className="assistant-widget">
      {open && <section className="assistant-panel" role="dialog" aria-label={t('chat.title')} aria-modal="false"
        onKeyDown={event => { if (event.key === 'Escape') { event.stopPropagation(); close(); } }}>
        <header className="assistant-heading">
          <Logo width={42} height={42} />
          <div><h2>{t('chat.title')}</h2><p><span className="assistant-status-dot" />{t('chat.subtitle')}</p></div>
          <button type="button" disabled={importBusy} className="assistant-icon-button" onClick={reset} aria-label={t('chat.clear')} title={t('chat.clear')}>↺</button>
          <button type="button" disabled={importBusy} ref={closeButton} className="assistant-icon-button" onClick={close} aria-label={t('chat.close')}>×</button>
        </header>
        {permitted && <div className="assistant-scope">
          <label>{t('chat.company')}<select value={companyId} disabled={loading || busy || importBusy} onChange={event => { setError(''); setCompanyId(event.target.value); }}>
            <option value="">{t('chat.chooseCompany')}</option>
            {companies.map(company => <option key={company.id} value={company.id}>{company.name}</option>)}
          </select></label>
          <label>{t('chat.deposit')}<select value={depositId} disabled={!scopeReady || busy || importBusy || depositsLoading} onChange={event => { reset(); setDepositId(event.target.value); }}>
            <option value="">{t('chat.chooseDeposit')}</option>
            {deposits.map(deposit => <option key={deposit.id} value={deposit.id}>{!companyId && deposit.companyName ? `${deposit.companyName} · ` : ''}{deposit.name}</option>)}
          </select></label>
        </div>}
        {!importOpen && !dailyOpen && permitted && <div className="assistant-tools">
          <button type="button" disabled={busy || !scopeReady} onClick={() => setDailyOpen(true)}>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 3v14h14M7 13V9m4 4V5m4 8V7" /></svg>{t('dailySales.title')}
          </button>
          {['ROLE_ADMIN', 'ROLE_WAREHOUSE'].includes(localStorage.getItem('role') || '') && <button type="button" disabled={busy || loading} onClick={() => setImportOpen(true)}>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 13V3m-4 4 4-4 4 4M3 12v5h14v-5" /></svg>{t('import.title')}
          </button>}
        </div>}
        <div className="assistant-messages" role={importOpen || dailyOpen ? undefined : 'log'} aria-live={importOpen || dailyOpen ? 'off' : 'polite'} aria-relevant="additions text" aria-busy={busy}>
          {dailyOpen ? <DailySalesPanel key={`${companyId}:${depositId}`} companyId={companyId} depositId={depositId} onClose={() => setDailyOpen(false)} /> : <>
          {importOpen ? <AssistantImport companyId={companyId} depositId={depositId} aiAvailable={!!status?.configured} onBusy={setImportBusy} onClose={() => { setImportOpen(false); reset(); }} /> : <>
          {messages.length === 0 && <div className="assistant-welcome">
            <span className="assistant-eyebrow">{t('chat.welcomeLabel')}</span>
            <h3>{t('chat.welcome')}</h3>
            <p>{t(permitted ? 'chat.intro' : 'chat.accessDenied')}</p>
            {permitted && <div className="assistant-suggestions">{['lowStock', 'stock', 'sales'].map(action =>
              <button type="button" key={action} disabled={!scopeReady || busy} onClick={() => void send(action)}>
                <span>{t(`chat.actions.${action}`)}</span><span aria-hidden="true">↗</span>
              </button>)}</div>}
            {scopeReady && status?.configured && <div className="assistant-suggestions">{['explainStock', 'explainSales'].map(question =>
              <button type="button" key={question} disabled={busy} onClick={() => void send(undefined, t(`chat.${question}`))}>{t(`chat.${question}`)}</button>)}</div>}
          </div>}
          {messages.map((message, index) => <article key={index} className={`assistant-message assistant-message-${message.role}`}>
            <span className="assistant-message-author">{t(message.role === 'user' ? 'chat.you' : 'chat.title')}</span>
            <p>{message.content}</p>
            {message.reply?.data && <div className="assistant-result">
              <span className="assistant-result-label">{t('chat.liveResults')}</span>
              {message.reply.data.items.length > 0 ? <div className="assistant-table-scroll"><table>
                <caption className="assistant-sr-only">{t(`chat.actions.${message.reply.action}`)}</caption>
                <thead><tr>{Object.keys(message.reply.data.items[0]).map(key => <th key={key} scope="col">{t(`chat.fields.${key}`, { defaultValue: key })}</th>)}</tr></thead>
                <tbody>{message.reply.data.items.map((item, row) => <tr key={row}>{Object.keys(message.reply!.data!.items[0]).map(key => <td key={key}>{item[key] ?? '—'}</td>)}</tr>)}</tbody>
              </table></div> : <p>{t('chat.noResults')}</p>}
              <p className="assistant-coverage">{t(`chat.coverage.${message.reply.action}`)}</p>
              {message.reply.data.hasMore && <p className="assistant-coverage">{t('chat.moreResults')}</p>}
            </div>}
            {message.reply?.sources && <AssistantSources sources={message.reply.sources} />}
          </article>)}
          {busy && <div className="assistant-thinking" role="status"><span />{t('chat.thinking')}</div>}
          <div ref={end} />
          </>}
          </>}
        </div>
        {error && <div className="assistant-error" role="alert">{t(`chat.${error}`)}</div>}
        {(workspaceError || depositError || statusError) && <div className="assistant-error" role="alert">
          {t(`chat.${workspaceError || depositError || 'statusUnavailable'}`)}{' '}
          <button type="button" disabled={loading || busy} onClick={() => setReload(value => value + 1)}>{t('chat.retry')}</button>
        </div>}
        {!loading && companiesLoaded && companies.length === 0 && <p className="assistant-notice">{t('chat.noCompanies')}</p>}
        {scopeReady && !depositsLoading && !depositError && deposits.length === 0 && <p className="assistant-notice">{t('chat.noDeposits')}</p>}
        {depositsLimited && <p className="assistant-notice">{t('chat.depositsLimited')}</p>}
        {!importOpen && !dailyOpen && permitted && messages.length > 0 && <div className="assistant-followups">{['lowStock', 'stock', 'sales'].map(action =>
          <button type="button" key={action} disabled={!scopeReady || busy} onClick={() => void send(action)}>{t(`chat.actions.${action}`)}</button>)}</div>}
        {loading && <p className="assistant-notice" role="status">{t('chat.loading')}</p>}
        {!loading && permitted && status && !status.configured && <p className="assistant-notice">{t('chat.notConfigured')}</p>}
        {!importOpen && !dailyOpen && <form className="assistant-composer" onSubmit={event => { event.preventDefault(); void send(); }}>
          <label className="assistant-sr-only" htmlFor="assistant-message">{t('chat.message')}</label>
          <textarea id="assistant-message" value={draft} maxLength={2000} rows={2} placeholder={t('chat.placeholder')}
            disabled={!status?.configured || !scopeReady || busy} onChange={event => setDraft(event.target.value)}
            onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); void send(); } }} />
          {busy ? <button type="button" className="assistant-send" aria-label={t('chat.stop')} onClick={() => { generation.current++; request.current?.abort(); setBusy(false); }}>■</button>
            : <button type="submit" className="assistant-send" disabled={!draft.trim() || !status?.configured || !scopeReady} aria-label={t('chat.send')}>↑</button>}
        </form>}
        <p className="assistant-footer">{t('chat.privacy')}</p>
      </section>}
      <button type="button" ref={launcher} className={`assistant-launcher${open ? ' is-open' : ''}`} aria-label={t(open ? 'chat.close' : 'chat.open')}
        aria-expanded={open} onClick={() => open ? close() : setOpen(true)}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path d="M20 11.5a8 8 0 0 1-8 8H5l-3 2v-10a9 9 0 0 1 18 0Z" strokeLinejoin="round" />
          <path d="M7 10h8M7 14h5" strokeLinecap="round" />
        </svg><span>{t('chat.launcher')}</span>
      </button>
    </div>
  );
}
